<script>
    import { onMount, onDestroy } from "svelte";

    import Select from "svelte-select/Select.svelte";

    import {
        Button,
        PageLoading,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import PatientDetailsSection from "./createEditSections/PatientDetailsSection.svelte";
    import PatientContactSection from "./createEditSections/PatientContactSection.svelte";
    import ProcedureDetailsSection from "./createEditSections/ProcedureDetailsSection.svelte";
    import InsuranceDetailsSection from "./createEditSections/InsuranceDetailsSection.svelte";
    import SurgeonDetailsSection from "./createEditSections/SurgeonDetailsSection.svelte";
    import NotesDetailsSection from "./createEditSections/NotesDetailsSection.svelte";

    import {
        setInputFilter,
        goToURL,
        goBack,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";
    import { cacheAccountPrePopulateSearchOptions } from "../../../Utils/utilCache.svelte";

    import {
        user,
        loggedInDoctorDetail,
        accountDoctors,
        prePopulateAccountOptions,
    } from "../../../stores";

    import { defaultState, DirectInsuranceType } from "../../../Utils/utilEnums.svelte";

    import { addErrorInput } from "../../PatientEncounter/helpers/encounterUtils.svelte";

    import { NoteTypes } from "../../../Utils/utilConstants.svelte";

    let isLoadingAccount = false,
        isLoadingDoctor = false,
        isCreatingPatientEncounter = false,
        patientDetails = {
            source: "WebApp",
            sex: 2,
            alerts: "",
            notes: "",
            state: defaultState,
            patientContacts: [],
        },
        patientEncounterDetails = {
            source: "WebApp",
            procedureName: "",
            lengthInHours: 0.25,
            billingStatus: 0,
            scheduledDate: null, // schedule date by default is NULL, if date picked, it MUST be FUTURE date
            neuraxialType: Number(0),
            typeOfBill: Number(0),
            quoteOnly: Boolean(true),
            notes: "",
        },
        selectedDoctorProvider = {},
        insuranceDetails = {},
        selectedSurgeon = {},
        selectedHospital = {},
        adminSelectedAccount = {},
        createSurgeonFilters = { state: defaultState };

    let isEnterFromCalendar = false;

    let errorFields = [];

    let patientSectionRef,
        patientContactRef,
        procedureDetailsRef,
        insuranceDetailsRef,
        surgeonDetailsRef;

    let patientContactDetails = { useAsContact: false };

    // if set TRUE then no validation check on submission
    let overrideValidation = false;

    const defaultOverseasFund = {
                                    id: 0,
                                    name: "Other Overseas Insurance"
                                };

    $: isLoading = isLoadingDoctor;

    $: isFlowEnableSearchDoctor =
        $user.role == 100 || $user.role == 80 || $user.role == 20;

    $: if (patientDetails.insuranceType == 15) {
        const patientMedicareNoField = document.getElementById(
            "patientMedicareNoField",
        );
        const patientMedicareRefNoField = document.getElementById(
            "patientMedicareRefNoField",
        );
        if (patientMedicareNoField) {
            patientMedicareNoField.style.outline = "";
            patientMedicareNoField.placeholder = "Medicare No.";
        }
        if (patientMedicareRefNoField) {
            patientMedicareRefNoField.style.outline = "";
            patientMedicareRefNoField.placeholder = "Medicare Ref No.";
        }
    } else updateInputFields();

    $: isValidPatientContact = _checkPatientContactValidation(
        patientContactDetails,
    );

    $: showSelectAccountPhase =
        isFlowEnableSearchDoctor &&
        !adminSelectedAccount.id &&
        !isEnterFromCalendar;

    $: if (showSelectAccountPhase)
        window.addEventListener("keypress", _handleKeyboardPress);
    else window.removeEventListener("keypress", _handleKeyboardPress);

    onMount(() => {
        if (!isFlowEnableSearchDoctor && $user.id && !$loggedInDoctorDetail.id)
            loadAccountByUserID($user.id);

        setTimeout(() => {
            updateInputFields();
        }, 1000);
    });

    onDestroy(() => {
        if (isFlowEnableSearchDoctor) {
            $loggedInDoctorDetail = {};
            $accountDoctors = [];
        }
    });

    const loadAccounts = async (filterText) => {
        isLoadingAccount = true;

        await cacheAccountPrePopulateSearchOptions();

        if (!filterText)
            return $prePopulateAccountOptions.length
                ? $prePopulateAccountOptions
                : [];

        return $prePopulateAccountOptions.length
            ? $prePopulateAccountOptions.filter(({ value }) =>
                  value.toLowerCase().includes(filterText.toLowerCase()),
              )
            : [];
    };

    const loadAccountByUserID = async (id) => {
        isLoadingDoctor = true;

        await apiMethods.getMethod.getAccountByUserId(id).then((res) => {
            if (res.id) {
                $loggedInDoctorDetail = { ...res };
                patientEncounterDetails.claimDelayHours = Number(
                    res.claimDelayHours,
                );
            }
        });

        isLoadingDoctor = false;
    };

    const updateInputFields = () => {
        setInputFilter(
            document.getElementById("patientMedicareNoField"),
            function (value) {
                return value ? /^[0-9-\s]+$/i.test(value) : true;
            },
            "Must be an integer",
        );

        setInputFilter(
            document.getElementById("patientMedicareRefNoField"),
            function (value) {
                return value ? /^[0-9]+$/.test(value) : true;
            },
            "Must be an integer",
        );

        setInputFilter(
            document.getElementById("insuranceDetailsMembershipNoField"),
            function (value) {
                return value ? /^[A-z0-9]+$/.test(value) : true;
            },
            "Must be either an integer or a character",
        );

        setInputFilter(
            document.getElementById("insuranceDetailsMembershipRefNoField"),
            function (value) {
                return value ? /^[0-9]+$/.test(value) : true;
            },
            "Must be an integer",
        );
    };

    const adminGoToCreatePatientEncounter = async () => {
        const accountId = adminSelectedAccount.selectedAccount.key;

         await apiMethods.getMethod.getAccountById(accountId).then((res) => {
            if (!res || res.error)
                showError(
                    "Failed to load selected doctor details, please refresh the page and try again and contact support if the issue persists.",
                );
            else {
                adminSelectedAccount = { ...res };
                $loggedInDoctorDetail = { ...res };
                patientEncounterDetails.claimDelayHours = Number(
                    res.claimDelayHours,
                );
            }
        });
    };

    const createPatient = async () => {
        if (await _checkInputFieldsValid()) {
            isCreatingPatientEncounter = true;

            _updatePatientDetails();

            _updatePatientInsuranceDetails();

            if (!patientEncounterDetails.scheduledTime)
                patientEncounterDetails.scheduledTime = "00:00:00";
            else if (
                patientEncounterDetails.scheduledTime.split(":").length < 3
            )
                patientEncounterDetails.scheduledTime += ":00";

            patientEncounterDetails.scheduledDate =
                patientEncounterDetails.scheduledDate
                    ? patientEncounterDetails.scheduledDate.split("T")[0] +
                      "T" +
                      patientEncounterDetails.scheduledTime
                    : null;

            await checkPatientProcess(patientDetails);
        }
    };

    const checkPatientProcess = async (pd) => {
        if (!pd.medicareNo) pd.medicareNo = "";
        if (!pd.membershipNo) pd.membershipNo = "";

        // check !delete patient by first & middle & last name && dob && medicareNo
        // await apiMethods.postMethod
        //     .matchPatient(pd, $loggedInDoctorDetail.id)
        //     .then(async (res) => {
        //         if (res.id) {
        //             patientDetails.id = res.id;
        //             if (isValidPatientContact) await updatePatientContact();
        //             _updatePatientEncounterDetails(res);
        //             createPatientEncounter();
        //         } else await createProcess(pd);
        //     });
        await createProcess(pd);
    };

    const createProcess = async (pd) => {
        let isPatientCreated = false;

        if (pd.id)
            await apiMethods.putMethod
                .updatePatient(pd)
                .then(async (res) => {
                    isCreatingPatientEncounter = false;
                    if (res.id) {
                        patientDetails = { ...res };
                        if (isValidPatientContact) await updatePatientContact();
                        _updatePatientEncounterDetails(res);
                        isPatientCreated = true;
                    } else showError(res.error);
                })
                .catch((err) => {
                    isCreatingPatientEncounter = false;
                    showError(err);
                });
        else
            await apiMethods.postMethod
                .createPatient(pd)
                .then(async (res) => {
                    isCreatingPatientEncounter = false;
                    if (res.id) {
                        patientDetails.id = res.id;
                        if (isValidPatientContact) await updatePatientContact();
                        _updatePatientEncounterDetails(res);
                        isPatientCreated = true;
                    } else showError(res.error);
                })
                .catch((err) => {
                    isCreatingPatientEncounter = false;
                    showException(err);
                });

        if (isPatientCreated) createPatientEncounter();
    };

    const createPatientEncounter = () => {
        const encounterHasNotes = patientEncounterDetails.notes.length > 0;

        let encounterNotes;

        if (encounterHasNotes) {
            encounterNotes = patientEncounterDetails.notes;
            delete patientEncounterDetails.notes;
        }

        apiMethods.postMethod
            .createPatientEncounter(patientEncounterDetails)
            .then(async (res) => {
                if (res.id) {
                    if (isEnterFromCalendar) goBack();
                    else if (encounterHasNotes)
                        await createEncounterNotes(encounterNotes, res.id);
                    else goToURL(`/PatientQuote/view/${res.id}`);
                } else {
                    showError(res.error);
                }
            })
            .then(() => (isCreatingPatientEncounter = false))
            .catch((err) => showException(err));
    };

    const createEncounterNotes = async (notes, encounterID) => {
        await apiMethods.postMethod
            .createNote({
                relTo: NoteTypes.PatientEncounter,
                relId: encounterID,
                content: notes,
                visibleToDoctors: true,
            })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to create encounter notes, please refresh the page and try again and contact support if the issue persists.",
                    );
                else oToURL(`/PatientQuote/view/${encounterID}`);
            })
            .catch((err) => showError(err));
    };

    const updatePatientContact = async () => {
        if (!patientContactDetails.id) await _addPatientContact();
        else _editPatientContact();
    };

    const getAccountOptionLabel = (option) => option.fullName;

    const clearCurrentAdminSelectedAccount = () => {
        adminSelectedAccount.selectedAccount = null;
    };

    const onHospitalChange = ({ detail }) => {
        if (detail?.id) _syncProviderWithHospital(detail);
    };

    const showDotActionsMenu = () => {
        if (document.getElementById("encounterActionMenu"))
            document.getElementById("encounterActionMenu").style.display =
                "flex";
    };

    const hideDotActionsMenu = () => {
        if (document.getElementById("encounterActionMenu"))
            document.getElementById("encounterActionMenu").style.display =
                "none";
    };

    // HELPER FUNCTIONS
    const _syncProviderWithHospital = (hospital) => {
        const { id: hospitalId } = hospital;

        const doctorProvidersArr = $loggedInDoctorDetail.providers;

        if (!doctorProvidersArr.length) return;

        const relatedHospital = doctorProvidersArr.find(
            ({ hospitals }) =>
                hospitals &&
                hospitals.length &&
                hospitals.some(
                    (hospital) => hospital.hospitalId == hospitalId,
                ),
        );

        if (relatedHospital) {
            selectedDoctorProvider.details = relatedHospital;
            if (
                relatedHospital.hospitals?.length &&
                patientDetails.insuranceType == DirectInsuranceType.PublicInPrivate
            ) {
                const pipHospital = relatedHospital.hospitals.find(
                    (h) => h.hospitalId == hospitalId
                );
                if (pipHospital)
                    publicInPrivateObj.vendorNumbers = pipHospital.pipVendorNumber ?? "";
            }
        }
    };

    const _updatePatientDetails = () => {
        const {
            title,
            birthDate,
            medicareRefNo,
            alternativeFirstName,
            alternativeLastName,
        } = patientDetails;

        if (title == "Title") patientDetails.title = 20;

        if (!birthDate) patientDetails.birthDate = "2999-01-01T00:00:00";

        if (medicareRefNo) patientDetails.medicareRefNo = Number(medicareRefNo);
        else patientDetails.medicareRefNo = null;

        if (!alternativeFirstName) patientDetails.alternativeFirstName = "";
        if (!alternativeLastName) patientDetails.alternativeLastName = "";
    };

    const _updatePatientInsuranceDetails = () => {
        const { insuranceType } = patientDetails;

        if (patientDetails.insurance) delete patientDetails.insurance;
        if (patientDetails.insuranceId) delete patientDetails.insuranceId;
        if (patientDetails.membershipNo) delete patientDetails.membershipNo;

        switch (insuranceType) {
            case 0: // Private
                const {
                    insuranceFund,
                    membershipNo,
                    membershipRefNo,
                    privateCoverType,
                } = insuranceDetails;
                if (insuranceFund?.id)
                    patientDetails.insuranceId = insuranceFund.id;

                patientDetails.membershipNo = membershipNo ?? "";

                patientDetails.membershipRefNo = membershipRefNo
                    ? Number(membershipRefNo)
                    : null;

                if (privateCoverType)
                    patientDetails.insurance = privateCoverType;
                break;
            case 10: // Overseas cover
                patientDetails.insurance = insuranceDetails.overseasCover;
                patientDetails.membershipNo = insuranceDetails.membershipNo;
                patientDetails.membershipRefNo =
                    insuranceDetails.membershipRefNo
                        ? Number(insuranceDetails.membershipRefNo)
                        : null;
                if (insuranceDetails.insuranceFund)
                    patientDetails.insuranceId =
                        insuranceDetails.insuranceFund.id;
                break;
            case 11: // Self insured
            case 15: // Cosmetic
                break;
            default:
                break;
        }
    };

    const _updatePatientEncounterDetails = (res) => {
        patientDetails = { ...res };

        patientEncounterDetails = {
            ...patientEncounterDetails,
            patient: { id: res.id },
            doctor: { id: $loggedInDoctorDetail.id },
            surgeon: !selectedSurgeon.details
                ? { id: 0 }
                : {
                      id: selectedSurgeon.details.id,
                      providerUniqueId:
                          selectedSurgeon.details.providerUniqueId,
                  },
            hospital: !selectedHospital.details
                ? { id: 0 }
                : { id: selectedHospital.details.id },
            doctorProvider: {
                number: selectedDoctorProvider.details.number,
            },
            patientAddress: res.address,
            patientEmail: res.email,
            patientPhone: res.phone,
            patientCountry: res.country,
            patientState: res.state,
            patientCity: res.city,
            patientPostCode: res.postCode,
        };
    };

    const _checkInputFieldsValid = async () => {
        errorFields = [];

        await _updateInvalidFieldsStyle();

        return errorFields.length == 0;
    };

    const _updateInvalidFieldsStyle = async () => {
        if (!selectedDoctorProvider.details) {
            errorFields = addErrorInput("doctorProviderContainer", errorFields);
            errorFields = addErrorInput(
                "doctorProviderContainer",
                errorFields,
                "Doctor provider",
            );
        }

        const patientDetailsCheck =
            await patientSectionRef.checkPatientDetailsValidation();

        if (patientDetailsCheck.length)
            errorFields = [...errorFields, ...patientDetailsCheck];

        const patientContactCheck =
            await patientContactRef.checkPatientContactValidation();

        if (patientContactCheck.length)
            errorFields = [...errorFields, ...patientContactCheck];

        const procedureDetailsCheck =
            await procedureDetailsRef.checkProcedureDetailsValidation();

        if (procedureDetailsCheck.length)
            errorFields = [...errorFields, ...procedureDetailsCheck];

        const insuranceDetailsCheck =
            await insuranceDetailsRef.checkInsuranceDetailsValidation(
                patientDetails,
            );

        if (insuranceDetailsCheck.length)
            errorFields = [...errorFields, ...insuranceDetailsCheck];

        // const surgeonDetailsCheck =
        //     await surgeonDetailsRef?.checkSurgeonDetailsValidation();

        // if (surgeonDetailsCheck?.length)
        //     errorFields = [...errorFields, ...surgeonDetailsCheck];

        if (errorFields.length) {
            const scrollToElement = document.getElementById(errorFields[0]);
            if (scrollToElement)
                scrollToElement.scrollIntoView({ behavior: "smooth" });
        }
    };

    const _addPatientContact = async () => {
        await apiMethods.postMethod
            .createPatientContact({
                ...patientContactDetails,
                patientId: patientDetails.id,
            })
            .catch((err) => showException(err));
    };

    const _editPatientContact = async () => {
        await apiMethods.putMethod
            .updatePatientContact(patientContactDetails)
            .catch((err) => showException(err));
    };

    const _checkPatientContactValidation = (contact = {}) => {
        return (
            contact.name ||
            contact.relationship ||
            contact.email ||
            contact.phone
        );
    };

    const _handleKeyboardPress = (e) => {
        if (!e) return;

        const { key } = e;

        if (
            !key ||
            key.toLowerCase() != "enter" ||
            !adminSelectedAccount.selectedAccount
        )
            return;

        adminGoToCreatePatientEncounter();
    };
</script>

{#if isLoading}
    <PageLoading />
{:else if showSelectAccountPhase}
    <div
        class="flex flex-col h-full flex-1 items-center justify-center gap-y-4"
    >
        <div class="flex gap-x-4 items-center w-3/5">
            <p>Select account:</p> 

            <Select
                bind:value={adminSelectedAccount.selectedAccount}
                class="flex flex-1 max-h-[35px] rounded-full"
                loadOptions={loadAccounts}
                itemId="key"
                label="value"
                on:clear={clearCurrentAdminSelectedAccount}
                noOptionsMessage="No account found"
                placeholder="Account"
                focused
            >
                <div slot="item" let:item>
                    <div title={`User ID: ${item.key}`}>
                        {item.value}
                    </div>
                </div>
            </Select>
        </div>

        <Button
            clickFunc={adminGoToCreatePatientEncounter}
            isDisabled={!adminSelectedAccount.selectedAccount}
        >
            Next step
        </Button>
    </div>
{:else}
    <div class="flex-1 flex mt-4 w-full">
        <form class="flex flex-col flex-1 gap-y-4 overflow-y-auto">
            <div class="flex flex-1 w-full items-center justify-end">
                <div class="flex items-center gap-x-2">
                    <Button
                        clickFunc={createPatient}
                        isDisabled={isCreatingPatientEncounter}
                        defaultClass="btn btn-sm btn-secondary text-white text-sm flex-none border-none w-auto h-10 min-w-[200px] mx-2"
                        manualClass={isCreatingPatientEncounter
                            ? "loading"
                            : ""}
                    >
                        {isCreatingPatientEncounter ? "Saving" : "Save"}
                    </Button>

                    <div class="flex-none" on:mouseleave={hideDotActionsMenu}>
                        <ul class="menu menu-horizontal p-0">
                            <li tabindex="0" class="justify-end">
                                <div
                                    class="flex"
                                    on:mouseenter={showDotActionsMenu}
                                >
                                    <div
                                        class="flex items-center justify-center"
                                    >
                                        <i class="fa-solid fa-ellipsis-vertical"
                                        ></i>
                                    </div>
                                </div>
                                <ul
                                    id="encounterActionMenu"
                                    class="z-[100] bg-secondary"
                                >
                                    <Button
                                        clickFunc={() => {
                                            overrideValidation =
                                                !overrideValidation;
                                        }}
                                        defaultClass="btn btn-sm btn-secondary text-white text-xs flex-none border-none w-auto h-10 px-1 min-w-[200px]"
                                    >
                                        <div
                                            class="flex items-center justify-between gap-x-2"
                                        >
                                            <input
                                                type="checkbox"
                                                class="toggle toggle-secondary toggle-sm"
                                                id="overrideValidationCheckbox"
                                                checked={overrideValidation}
                                                on:click={(e) => {
                                                    e.stopPropagation();
                                                    overrideValidation =
                                                        !overrideValidation;
                                                }}
                                            />
                                            <label
                                                for="overrideValidationCheckbox"
                                                class="cursor-pointer text-xs"
                                            >
                                                Override Validation
                                            </label>
                                        </div>
                                    </Button>
                                </ul>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <PatientDetailsSection
                bind:this={patientSectionRef}
                bind:patientDetails
                bind:patientEncounterDetails
                bind:patientContactDetails
                bind:insuranceDetails
                {insuranceDetailsRef}
                isFromCreate
                {overrideValidation}
            />

            <PatientContactSection
                bind:this={patientContactRef}
                bind:patientContactDetails
                bind:patientEncounterDetails
                {overrideValidation}
            />

            <ProcedureDetailsSection
                bind:this={procedureDetailsRef}
                bind:patientEncounterDetails
                bind:selectedSurgeon
                bind:selectedHospital
                bind:createSurgeonFilters
                on:onHospitalChange={onHospitalChange}
                typeOfBill={patientEncounterDetails.typeOfBill}
                isCreate
                {overrideValidation}
            />

            <InsuranceDetailsSection
                bind:this={insuranceDetailsRef}
                bind:patientDetails
                bind:insuranceDetails
                bind:selectedHospital
                bind:selectedDoctorProvider
                bind:createSurgeonFilters
                {overrideValidation}
            />

            <!-- <SurgeonDetailsSection
                bind:this={surgeonDetailsRef}
                bind:selectedSurgeon
                bind:selectedHospital
                bind:createSurgeonFilters
                on:onHospitalChange={onHospitalChange}
                isCreate
            /> -->

            <NotesDetailsSection bind:patientEncounterDetails />

            <Button
                clickFunc={createPatient}
                isDisabled={isCreatingPatientEncounter}
                defaultClass="btn btn-sm btn-secondary text-white text-sm flex-none border-none w-auto h-10 min-w-[200px] mx-2"
                manualClass={isCreatingPatientEncounter ? "loading" : ""}
            >
                {isCreatingPatientEncounter ? "Saving" : "Save"}
            </Button>
        </form>
    </div>
{/if}
